// Catch-up widgets (CATCHUP_WIDGETS_PLAN.md). Pure data — safe for API + web.
// Generalizes the missed-dose backfill pattern (MISSED_DOSE_BACKFILL_PLAN.md) to
// bowel, PT, sleep, gym, and a reminder-only vitals widget.

export const CATCHUP_TYPES = ["medication", "bowel", "pt", "sleep", "gym", "vitals", "measurement"] as const;
export type CatchUpType = (typeof CATCHUP_TYPES)[number];

/** Per-type widget toggles stored in proactiveSuggestions.catchUpWidgets. Absent key -> true. */
export type CatchUpWidgetSettings = Partial<Record<CatchUpType, boolean>>;

export const CATCHUP_LOOKBACK_DAYS = 7;
export const MAX_CATCHUP_LOG_ENTRIES = 30;

// Eligibility gates (D-CU-6 / A1)
export const BOWEL_ACTIVE_MIN_EVENTS = 5; // BM events required...
export const BOWEL_ACTIVE_WINDOW_DAYS = 30; // ...in this window, else widget hides
export const SLEEP_ACTIVE_MIN_NIGHTS = 5; // overnight events required...
export const SLEEP_ACTIVE_WINDOW_DAYS = 14; // ...in this window
export const VITALS_ACTIVE_MIN_DAYS = 5; // distinct local days a kind was logged...
export const VITALS_ACTIVE_WINDOW_DAYS = 14; // ...in this window
export const VITALS_GAP_MIN_DAYS = 2; // days since last log before the reminder shows
export const MEASUREMENT_ACTIVE_MIN_SESSIONS = 2; // measuring sessions required...
export const MEASUREMENT_ACTIVE_WINDOW_DAYS = 180; // ...in this window, else widget hides

export interface BowelCatchUpSection {
  kind: "bowel";
  /** Local days (YYYY-MM-DD, newest first) in today-7...yesterday with no bowel_movement event. */
  gapDays: string[];
}

export interface SleepCatchUpSection {
  kind: "sleep";
  /** nightKeys (YYYY-MM-DD of the morning the night ended, newest first) with no overnight sleep event. */
  gapNights: string[];
  /** Prefill defaults learned from the last 14 overnight events (A3). Local clock times "HH:MM". */
  typicalBedTime: string;
  typicalWakeTime: string;
  typicalQuality: number; // 1..10 median
}

export interface PtCatchUpGap {
  programEventId: string;
  programLabel: string; // sheetTitle ?? suspectedDiagnosis ?? bodyRegion ?? "PT Program"
  dayKey: string; // local day with no session for this program
}
export interface PtWeeklyShortfall {
  programEventId: string;
  programLabel: string;
  expectedPerWeek: number; // from cadence, e.g. 3 for "3x/week"
  loggedLastWeek: number; // distinct session days in the past 7 local days
}
export interface PtCatchUpSection {
  kind: "pt";
  gaps: PtCatchUpGap[]; // daily-cadence programs (D-CU-16), newest first
  weekly: PtWeeklyShortfall[]; // per-week-cadence programs with logged < expected
}

export interface GymCatchUpSection {
  kind: "gym";
  programTitle: string;
  plannedDaysPerWeek: number; // intake.daysPerWeek
  loggedDaysLastWeek: number; // distinct local days with any gym_exercise, past 7 days
}

export interface VitalsReminderItem {
  vitalKind: string; // payload kind value as stored
  label: string; // display label
  lastLoggedDayKey: string; // local day of the most recent log
  gapDays: number; // whole local days since then
}
export interface VitalsCatchUpSection {
  kind: "vitals";
  items: VitalsReminderItem[];
}

export interface MeasurementCatchUpSection {
  kind: "measurement";
  lastMeasuredAt?: string;
  daysSince: number;
  reminderDays: number;
}

export type CatchUpSection =
  | BowelCatchUpSection
  | SleepCatchUpSection
  | PtCatchUpSection
  | GymCatchUpSection
  | VitalsCatchUpSection
  | MeasurementCatchUpSection;

export interface CatchUpResponse {
  sections: CatchUpSection[]; // only enabled + eligible + non-empty sections
  generatedAt: string;
}

/** One user decision sent to POST /catch-up/log (discriminated union). */
export type CatchUpLogEntry =
  | {
      kind: "bowel";
      dayKey: string;
      action: "logged" | "none";
      /** For action "logged": user-edited instant; default noon local (D-CU-9). Ignored for "none". */
      occurredAt?: string;
      bristolScale?: number;
      notes?: string;
    }
  | { kind: "sleep"; nightKey: string; startedAt: string; endedAt: string; qualityRating: number }
  | {
      kind: "pt";
      programEventId: string;
      dayKey: string;
      did: boolean;
      /** For did=true: user-edited instant; default noon local. */
      performedAt?: string;
    };

export interface CatchUpLogResult {
  saved: { eventId: string; eventType: string; occurredAt: string; entryKey: string }[];
  skipped: { entryKey: string; reason: string }[];
}
